/**
 * Creates a fake Worker backed by a MessageChannel for use when the rendered
 * scene runs on the main thread (isMobileApp === true) instead of in a Worker.
 *
 * The returned `worker` object satisfies the Worker interface and is stored in
 * context as `renderedWorker` — all existing postMessage / addEventListener
 * calls in useRenderedListeners and DiceCanvases continue to work unchanged.
 *
 * The returned `port` (MessageChannel.port2) is passed as a prop to
 * <RenderedScene> so it can exchange messages via the channel instead of via
 * `self` (WorkerGlobalScope).
 */
class MainThreadRenderedWorker implements Worker {
  readonly #port: MessagePort;

  onmessage: ((this: Worker, ev: MessageEvent) => unknown) | null = null;
  onmessageerror: ((this: Worker, ev: MessageEvent) => unknown) | null = null;
  onerror: ((this: AbstractWorker, ev: ErrorEvent) => unknown) | null = null;

  constructor(port: MessagePort) {
    this.#port = port;
  }

  postMessage(
    message: unknown,
    transfer?: Transferable[] | StructuredSerializeOptions,
  ): void {
    if (Array.isArray(transfer)) {
      this.#port.postMessage(message, transfer);
    } else {
      this.#port.postMessage(message);
    }
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void {
    this.#port.addEventListener(type, listener, options);
  }

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void {
    this.#port.removeEventListener(type, listener, options);
  }

  dispatchEvent(event: Event): boolean {
    return this.#port.dispatchEvent(event);
  }

  terminate(): void {
    this.#port.close();
  }
}

export const createMainThreadRenderedWorker = (): {
  worker: Worker;
  port: MessagePort;
} => {
  const { port1, port2 } = new MessageChannel();
  port1.start();
  port2.start();

  return {
    // port1 is exposed as the "Worker" to existing consumer code
    worker: new MainThreadRenderedWorker(port1),
    // port2 is given to <RenderedScene> to use instead of `self`
    port: port2,
  };
};
